import { Col, Divider, Flex, Row, Typography } from "antd"
import { EnvironmentOutlined } from "@ant-design/icons"
import { useTitle } from "hooks/useTitle"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { getGuideTrip } from "store/trips"
import { formatExperienceTerms } from "utils/experience"
import { buildPreviewPayload } from "../mappers/toApiPayload"
import { buildPreviewAvailability } from "../helpers/previewAvailability"
import { useWizardContext } from "../WizardContext"
import { PreviewAbout } from "pagesV2/pro/Experiences/ListingPreview/PreviewAbout"
import { PreviewGuide } from "pagesV2/pro/Experiences/ListingPreview/PreviewGuide"
import { PreviewMediaListing } from "pagesV2/pro/Experiences/ListingPreview/PreviewMedia"
import { PreviewMeetingLocation } from "pagesV2/pro/Experiences/ListingPreview/PreviewMeetingLocation"
import ActionsPanel from "pagesV2/pro/Experiences/ListingPreview/ActionsPanel"
import { PreviewAboutV2 } from "../components/preview/PreviewAboutV2/PreviewAboutV2"
import { PreviewTicketTypes } from "../components/preview/PreviewTicketTypes/PreviewTicketTypes"
import { RealTimeAvailability } from "../components/preview/RealTimeAvailability/RealTimeAvailability"
import styles from "pagesV2/pro/Experiences/ListingPreview/ExperiencesPreview.module.css"
import { routes } from "router"
import { EXPERIENCE_TYPES } from "constants/experiences"

const { Text } = Typography

const { Title } = Typography

/**
 * Preview step — renders the public-listing preview using the shared
 * ListingPreview components, matching the Figma "Preview Listing" design.
 *
 * By the time the user reaches this step every prior tab has been saved to the
 * backend, so the server `trip` already reflects all edits. We merge the
 * still-local media (mediaFilesRef) on top so unsaved photos/videos show too,
 * then drive the shared sections from that `previewTrip`.
 *
 * Layout mirrors ListingPreview/index.jsx:
 *   PreviewAddress         → location / capacity / license / hours
 *   PreviewMediaListing    → photo + video gallery
 *   PreviewAbout           → about, what's included/bring, not allowed, skills, other
 *   PreviewMeetingLocation → map + meeting instructions
 *   PreviewGuide           → "Guided by" host card
 *   BookingDetailCard      → "Real-Time Availability" booking panel (right column)
 *
 * When opened via the ExperienceCard preview button (readOnlyPreview=true),
 * the right column shows ActionsPanel instead of the booking card.
 */
export const PreviewStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { form, mediaFilesRef, stepSaveHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))
  const experienceOptions = useSelector((state) => state.trips.experienceOptions?.data)

  const isPreviewMode = searchParams.get("readOnlyPreview") === "true"
  const routeType = searchParams.get("route")
  const isTicketed = searchParams.get("type") === EXPERIENCE_TYPES.ticketed || trip?.experience_type === EXPERIENCE_TYPES.ticketed

  // Loader watcher for trips.trip is provided by the shared PreviewAddress below;
  // registering it here too would render a second overlapping loader.

  useEffect(() => {
    if (id) dispatch(getGuideTrip(id, true, true))
  }, [id, dispatch])

  // Register save handler — marks wizard as complete when user publishes
  useEffect(() => {
    stepSaveHandlersRef.current["preview"] = async () => {
      return buildPreviewPayload()
    }
    return () => {
      delete stepSaveHandlersRef.current["preview"]
    }
  }, [stepSaveHandlersRef])

  const previewTrip = useMemo(() => {
    // skill_levels stores IDs — resolve to names via the dynamic backend options
    // so the shared PreviewAbout shows names, not IDs.
    const skillNameById = new Map((experienceOptions?.skill_levels || []).map((level) => [level.id, level.name]))
    const skill_levels = (trip?.skill_levels || []).map((level) =>
      level && typeof level === "object" ? level.name : skillNameById.get(level) ?? level
    )

    // Merge still-local media on top of the server trip so unsaved photos/videos
    // also appear. If the Media tab hasn't been opened, mediaFilesRef is empty —
    // keep the server trip's media so existing images still show in the preview.
    const activeMedia = (mediaFilesRef.current || []).filter((media) => !media._destroy)
    const mediaFields = activeMedia.length > 0
      ? {
          photos: activeMedia.filter((media) => media.file_type === "photo"),
          cropped_photos: activeMedia.filter((media) => media.file_type === "cropped_photo"),
          videos: activeMedia.filter((media) => media.file_type === "video"),
          trimmed_videos: activeMedia.filter((media) => media.file_type === "trimmed_video")
        }
      : {}

    return { ...trip, skill_levels, ...mediaFields }
  }, [trip, mediaFilesRef, experienceOptions])

  const title = form.getFieldValue("title") || trip?.title
  useTitle(title)

  // Availability inputs for the Real-Time Availability calendar. Prefer the
  // wizard form (reflects the latest edits); fall back to the server trip.
  const durationInfo = useMemo(() => {
    const durationType = form.getFieldValue("duration_type") || trip?.duration_type || "single_day"
    // trip.duration_value is only ever set for single-length legacy/ticketed durations — a
    // dynamic (min/max range) or fixed-with-multiple-options multi-day trip always has it nil,
    // which rendered as a literal "null day experience". formatExperienceTerms already handles
    // all three multi-day shapes correctly (it's what "3-5 days"/"3, 5 days" elsewhere on this
    // same preview page comes from) — reuse it here instead of re-deriving duration_value.
    const numberOfDays =
      form.getFieldValue("number_of_days") ||
      (trip?.duration_type === "multi_day" ? formatExperienceTerms(trip, t)?.replace(/\s*days?$/i, "") : undefined)
    const durationValue = form.getFieldValue("duration_value") ?? trip?.duration_value
    const durationUnit = form.getFieldValue("duration_unit") ?? trip?.duration_unit
    return { durationType, numberOfDays, durationValue, durationUnit }
  }, [form, trip, t])

  const availability = useMemo(() => {
    const isMultiDay = durationInfo.durationType === "multi_day"
    const schedules = (
      form.getFieldValue("availability_schedules") ||
      trip?.trip_availability_schedules ||
      trip?.availability_schedules ||
      []
    ).filter((schedule) => !schedule._destroy)
    // A multi-day listing's start days live in their own field; a single-day one's in its schedules.
    const multiDayStartDays =
      form.getFieldValue("multi_day_start_days") ||
      schedules.find((schedule) => schedule.schedule_type === "multi_day")?.days ||
      []
    const weekdays = isMultiDay ? multiDayStartDays : schedules.flatMap((schedule) => schedule.days || [])

    const isSeasonal = form.getFieldValue("isSeasonalExperience") ?? trip?.seasonal ?? false
    const timeBlocks = form.getFieldValue("timeBlocks") || []
    let seasonRanges = timeBlocks
      .map((blockIndex) => {
        const start = form.getFieldValue(`seasonStart_${blockIndex}`)
        const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
        return start && end ? { start, end } : null
      })
      .filter(Boolean)
    if (!seasonRanges.length && trip?.available_date_ranges?.length) {
      seasonRanges = trip.available_date_ranges
        .map((range) => ({ start: range.start, end: range.end }))
        .filter((range) => range.start && range.end)
    }

    return buildPreviewAvailability({
      trip,
      isTicketed,
      isMultiDay,
      spanDays: Number(durationInfo.numberOfDays) || 1,
      timeSlots: (form.getFieldValue("time_slots") || trip?.time_slots || []).filter((slot) => !slot._destroy),
      cutOffValue: form.getFieldValue("cut_off_value") ?? trip?.cut_off_value,
      cutOffUnit: form.getFieldValue("cut_off_unit") ?? trip?.cut_off_unit,
      weekdays,
      isSeasonal,
      isRecurringYearly: form.getFieldValue("recurringYearly") ?? trip?.recurring_yearly ?? false,
      seasonRanges
    })
  }, [form, trip, isTicketed, durationInfo])

  // Edit the listing (not a new one): the wizard is in edit mode only with ?route=, as from the list.
  const handleEdit = () => {
    const listingType = isTicketed ? EXPERIENCE_TYPES.ticketed : EXPERIENCE_TYPES.private
    navigate(routes.proEditExperienceV2Path({ id: trip?.id }, { route: routeType, type: listingType }))
  }

  return (
    <>
      <Divider style={{ marginTop: 0, marginBottom: 24 }} />
      <div style={{ position: "relative", paddingRight: "100px" }}>
        <Title level={3} style={{ lineHeight: 1, marginBottom: 0 }}>
          {title}
        </Title>
      </div>
      {/* Title section: only the location line — no other details. */}
      {previewTrip?.address && (
        <Flex align="center" gap={8} style={{ marginTop: 8, marginBottom: 9 }}>
          <EnvironmentOutlined />
          <Text>{previewTrip.address}</Text>
        </Flex>
      )}

      <Row gutter={[10, 10]}>
        <Col span={24} lg={24} xl={15} style={{ marginBottom: "20px" }}>
          {/* Media lives inside the left column so the availability calendar in
              the right column aligns at the top, opposite the photos (Figma). */}
          <PreviewMediaListing trip={previewTrip} />
          {/* v2 About header (tag + meta + description); shared PreviewAbout renders
              the remaining card sections with its own About header hidden. boats is
              emptied so the Equipment section is excluded from the preview. */}
          <PreviewAboutV2 trip={previewTrip} />
          <PreviewTicketTypes />
          <PreviewAbout trip={{ ...previewTrip, boats: [] }} t={t} hideHeader />
          <PreviewMeetingLocation trip={previewTrip} t={t} />
          <PreviewGuide trip={previewTrip} t={t} />
          <Text style={{ display: "block", marginTop: 24, color: "var(--color-text-secondary)", fontSize: 14 }}>
            {t("listings.tabs.preview.powered_by", { defaultValue: "Powered by Digital Sportsman" })}
          </Text>
        </Col>
        <Col span={24} lg={24} xl={9} className={styles.formCard}>
          {isPreviewMode ? (
            <ActionsPanel trip={previewTrip} onEdit={handleEdit} routeType={routeType} />
          ) : (
            <RealTimeAvailability
              isDateAvailable={availability.isDateAvailable}
              firstAvailableDate={availability.firstAvailableDate}
              durationType={durationInfo.durationType}
              durationValue={durationInfo.durationValue}
              durationUnit={durationInfo.durationUnit}
              numberOfDays={durationInfo.numberOfDays}
              trip={previewTrip}
            />
          )}
        </Col>
      </Row>
    </>
  )
}
